import { useCallback, useEffect, useState } from 'react'
import { useAppDispatch } from 'state'
import { setCode, useAlgoEditorState } from 'state/slices/algoEditorSlice'
import { useDebounce } from 'usehooks-ts'

import { removeTab } from './string'

const handelStorageType = ({
  codeStoreIn,
  defaultCode,
  key
}: {
  key: string
  codeStoreIn: 'sessionStorage' | 'localStorage'
  defaultCode: string
}) => {
  let storageValue
  let storageGetCode: string
  let storageSetCode: (_code: string) => void

  switch (codeStoreIn) {
    case 'sessionStorage':
      storageValue = sessionStorage.getItem(key)
      storageGetCode = storageValue
        ? JSON.parse(storageValue).code
        : defaultCode

      storageSetCode = (code: string) =>
        sessionStorage.setItem(key, JSON.stringify({ code }))
      break
    case 'localStorage':
      storageValue = localStorage.getItem(key)
      storageGetCode = storageValue
        ? JSON.parse(storageValue).code
        : defaultCode
      storageSetCode = (code: string) =>
        localStorage.setItem(key, JSON.stringify({ code }))
      break
  }
  return { storageGetCode, storageSetCode }
}

/**
 * This sync value of code with local storage with debounce
 * @param key Should be something like ques-${id}
 * @param defaultValue Default value for this
 * @param codeStoreIn Where to store the code
 */

const useCode = ({
  key,
  codeStoreIn,
  defaultCode
}: {
  key: string
  codeStoreIn: 'sessionStorage' | 'localStorage'
  defaultCode: string
}) => {
  const [loadedForKey, setLoadedForKey] = useState('')

  const { code } = useAlgoEditorState()
  const dispatch = useAppDispatch()
  const updateCode = useCallback(
    (_code: string) => {
      dispatch(setCode(_code))
    },
    [dispatch]
  )

  const debouncedCode = useDebounce(code, 200) // `code` but wrapped in debounce

  useEffect(() => {
    if (key === loadedForKey) {
      return
    }
    const { storageGetCode } = handelStorageType({
      key,
      codeStoreIn,
      defaultCode
    })
    if (key.includes('python')) {
      updateCode(removeTab(storageGetCode))
    } else {
      updateCode(storageGetCode)
    }
    setLoadedForKey(key)
  }, [codeStoreIn, defaultCode, key, loadedForKey, updateCode])

  /**
   * Store the code in editor to storage with debounce
   */
  useEffect(() => {
    const { storageSetCode } = handelStorageType({
      key,
      codeStoreIn,
      defaultCode
    })
    storageSetCode(debouncedCode)
  }, [codeStoreIn, debouncedCode, defaultCode, key])

  return [code, updateCode] as const
}

export default useCode
